import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  apiGetBand, apiGetBandFiles, apiPutTourPromoContent, apiGetTour,
  type PromoContentDTO, type BandFileDTO,
} from '../api';
import PromoContentEditor from './PromoContentEditor';

const emptyContent = (): PromoContentDTO => ({ quotes: [], sections: [] });
const parse = (raw?: string | null): PromoContentDTO | null => {
  if (!raw) return null;
  try { const c = JSON.parse(raw); return c && typeof c === 'object' ? c : null; } catch { return null; }
};

/**
 * "Promo page" authoring panel of a TOUR (option C, 2026-10-19) — opens under
 * its tour card on the Tours page (same pattern as the budget panel). Edits
 * the TOUR's blob (including its tour title); "Copy from band default"
 * pre-fills it from `bands.promo_content` so the band text is a starting
 * point, not a duplicate to retype. Saved through
 * `PUT /api/tours/:id/promo-content` (editor+ on the tour's band).
 */
export default function TourPromoPanel({ tourId, bandId, canWrite }: { tourId: string | number; bandId: string; canWrite: boolean }) {
  const { t } = useTranslation();
  const [content, setContent] = useState<PromoContentDTO>(emptyContent);
  const [loaded, setLoaded] = useState(false);
  const [files, setFiles] = useState<BandFileDTO[]>([]);
  const [bandContent, setBandContent] = useState<PromoContentDTO | null>(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    apiGetBand(bandId)
      .then((b) => {
        if (cancelled) return;
        setBandContent(parse(b.promo_content));
        return apiGetBandFiles(bandId, null, 'promo_kit').then((rows) => { if (!cancelled) setFiles(rows || []); });
      })
      .catch(() => { /* editor works without files */ })
      .finally(() => { if (!cancelled) setLoaded(true); });
    // The tour list already carries promo_content (SELECT *); fetch fresh here
    // so a just-saved band blob / another session's edit is what we edit.
    apiGetTour(String(tourId))
      .then((tour) => { if (!cancelled) setContent(parse(tour.promo_content) || emptyContent()); })
      .catch(() => { /* keep the empty draft */ });
    return () => { cancelled = true; };
  }, [tourId, bandId]);

  const save = async () => {
    if (!canWrite || saving) return;
    setSaving(true); setMsg(null); setError(null);
    try {
      const r = await apiPutTourPromoContent(tourId, content);
      setContent(r.content || emptyContent());
      setMsg(t('promoContent.saved'));
    } catch (e) {
      setError(e instanceof Error ? e.message : t('promoContent.saveFailed'));
    } finally { setSaving(false); }
  };

  if (!loaded) return <p className="text-dim">{t('tours.loading')}</p>;

  return (
    <div data-tour-promo={String(tourId)}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        <strong>{t('promoContent.tourEditorTitle')}</strong>
        {bandContent && (
          <button
            type="button"
            className="btn btn-secondary btn-small"
            disabled={!canWrite || saving}
            onClick={() => setContent({ ...bandContent })}
          >
            {t('promoContent.copyFromBand')}
          </button>
        )}
      </div>
      <PromoContentEditor value={content} onChange={setContent} files={files} showTitle disabled={!canWrite || saving} />
      <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
        <button className="btn btn-primary" type="button" onClick={() => void save()} disabled={!canWrite || saving}>
          {saving ? t('promoContent.saving') : t('promoContent.save')}
        </button>
      </div>
      {msg && <p style={{ color: 'var(--success)', margin: '8px 0 0', fontSize: 'var(--font-sm)' }} data-tour-promo-msg>{msg}</p>}
      {error && <p style={{ color: 'var(--danger)', margin: '8px 0 0', fontSize: 'var(--font-sm)' }} role="alert">{error}</p>}
    </div>
  );
}
